/**
 * useTranslation hook
 *
 * Returns the translation object for the current language stored in kioskStore.
 * Also exposes a formatCurrency helper for IDR amounts.
 */

import { useKioskStore } from '../store/kioskStore';
import { translations, type Translations } from '../i18n/translations';

interface UseTranslationReturn {
  t: Translations;
  locale: 'id' | 'en';
  formatCurrency: (amount: number) => string;
  formatDate: (dateStr: string) => string;
}

export function useTranslation(): UseTranslationReturn {
  const language = useKioskStore((s) => s.language);
  const t = translations[language];

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr; // fallback to raw string for invalid dates
    const localeMap: Record<typeof language, string> = {
      id: 'id-ID',
      en: 'en-US',
    };
    const dateLocale = localeMap[language];
    return d.toLocaleString(dateLocale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jakarta',
    });
  };

  return { t, locale: language, formatCurrency, formatDate };
}
